/**
 * UserTypeStep — Step 2 of the ProfileComplete v2.0 wizard.
 *
 * Density redesign matches BasicInfoStep:
 *   - p-inputtext-sm for Dropdown / MultiSelect
 *   - Single uppercase caption per section (no icon+title+subtitle stack)
 *   - Compact checkbox groups (text-xs label, tight gaps)
 *   - mb-2 within sections, mb-4 between sections
 *
 * Preserved verbatim from legacy: same userType state shape, same area-search
 * endpoints, same progressive disclosure (loan-agent fields show on
 * loan_agent/dsa, referral fields show whenever ≥1 user_type is selected).
 */
import { useEffect, useRef, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Panel } from 'primereact/panel';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';

const USER_TYPE_OPTIONS = [
    { label: 'Loan Agent',                 value: 'loan_agent' },
    { label: 'DSA',                        value: 'dsa' },
    { label: 'Financial Consultant',       value: 'financial_consultant' },
    { label: 'Insurance Agent',            value: 'insurance_agent' },
    { label: 'Investment Advisor',         value: 'investment_advisor' },
    { label: 'Tax Consultant',             value: 'tax_consultant' },
    { label: 'Real Estate Agent',          value: 'real_estate_agent' },
    { label: 'Other',                      value: 'other' },
];

/* Roles offered to company employees instead of the agent/DSA list above.
   Stored in the same `user_type_selections` JSON array; the backend validator
   accepts both sets of values. Multi-select like the owner flow. */
const EMPLOYEE_USER_TYPE_OPTIONS = [
    { label: 'Sales',               value: 'sales' },
    { label: 'Operations',          value: 'operations' },
    { label: 'Backend',             value: 'backend' },
    { label: 'Document Collection', value: 'document_collection' },
    { label: 'Team Leader',         value: 'team_leader' },
    { label: 'Management',          value: 'management' },
];

const LOAN_PRODUCT_OPTIONS = [
    { label: 'Home Loan',     value: 'home_loan' },
    { label: 'Personal Loan', value: 'personal_loan' },
    { label: 'Business Loan', value: 'business_loan' },
    { label: 'Vehicle Loan',  value: 'vehicle_loan' },
    { label: 'Education Loan', value: 'education_loan' },
    { label: 'Gold Loan',     value: 'gold_loan' },
    { label: 'Credit Card',   value: 'credit_card' },
];

const DISBURSEMENT_OPTIONS = [
    { label: 'Less than ₹1 Lakh',     value: 'less_than_1_lakh' },
    { label: '₹1 Lakh – ₹10 Lakh',    value: '1_lakh_to_10_lakh' },
    { label: '₹10 Lakh – ₹50 Lakh',   value: '10_lakh_to_50_lakh' },
    { label: '₹50 Lakh – ₹1 Crore',   value: '50_lakh_to_1_crore' },
    { label: '₹1 – 5 Crore',          value: '1_to_5_crore' },
    { label: 'More than ₹5 Crore',    value: 'more_than_5_crore' },
];

const AREA_DEBOUNCE_MS = 500;

/* PrimeReact's `p-inputtext-sm` className doesn't propagate to Dropdown /
   MultiSelect in this theme — the wrapper components ignore it. Use `pt`
   to apply small sizing on the trigger so it renders at the same height
   (~32px) as <InputText p-inputtext-sm>.
   - Dropdown uses `input` for its trigger label
   - MultiSelect uses `label` for its trigger label
   - Setting both keys is safe; each component uses only the relevant one. */
const COMPACT_TRIGGER_STYLE = { padding: '0.4375rem 0.75rem', fontSize: '0.875rem' };
const SMALL_PT = {
    root: { className: 'p-inputtext-sm' },
    input: { style: COMPACT_TRIGGER_STYLE },
    label: { style: COMPACT_TRIGGER_STYLE },
};

const UserTypeStep = ({ value, errors, onChange, onErrors, saving = false, isCompanyEmployee = false }) => {
    const [activeAreas, setActiveAreas] = useState([]);
    const [supportAreas, setSupportAreas] = useState([]);
    const [activeAreasLoading, setActiveAreasLoading] = useState(false);
    const [supportAreasLoading, setSupportAreasLoading] = useState(false);
    const activeDebounce = useRef(null);
    const supportDebounce = useRef(null);

    const isAgentOrDSA = value.user_type_selections?.some((v) => v === 'loan_agent' || v === 'dsa');
    const hasAnySelection = (value.user_type_selections?.length || 0) > 0;

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axiosInstance.get('/location/areas/all?limit=50');
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setActiveAreas(res.data.data);
                    setSupportAreas(res.data.data);
                }
            } catch { /* silent */ }
        };
        load();
    }, []);

    const searchAreas = (query, setOptions, setLoading) => async () => {
        if (!query || query.length < 2) {
            setLoading(true);
            try {
                const res = await axiosInstance.get('/location/areas/all?limit=100');
                if (res.data?.success) setOptions(res.data.data || []);
            } catch { setOptions([]); }
            finally { setLoading(false); }
            return;
        }
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/location/areas/search?q=${encodeURIComponent(query)}&limit=100`);
            setOptions(res.data?.success ? (res.data.data || []) : []);
        } catch { setOptions([]); }
        finally { setLoading(false); }
    };

    const onActiveAreasFilter = (e) => {
        if (activeDebounce.current) clearTimeout(activeDebounce.current);
        activeDebounce.current = setTimeout(searchAreas(e.filter, setActiveAreas, setActiveAreasLoading), AREA_DEBOUNCE_MS);
    };
    const onSupportAreasFilter = (e) => {
        if (supportDebounce.current) clearTimeout(supportDebounce.current);
        supportDebounce.current = setTimeout(searchAreas(e.filter, setSupportAreas, setSupportAreasLoading), AREA_DEBOUNCE_MS);
    };

    const toggleSelection = (field, optionValue, checked) => {
        const current = value[field] || [];
        const next = checked ? [...current, optionValue] : current.filter((v) => v !== optionValue);
        onChange({ [field]: next });
        if (errors[field] && next.length > 0) onErrors({ [field]: '' });
    };

    return (
        <div>
            {/* ─ Section A — What defines you? ─────────────────────── */}
            <SectionPanel
                icon="pi-id-card"
                title="What defines you"
                subtitle="Pick all that apply — we'll tailor the rest of the form."
            >
                <CheckboxGrid
                    name="user_type"
                    options={isCompanyEmployee ? EMPLOYEE_USER_TYPE_OPTIONS : USER_TYPE_OPTIONS}
                    value={value.user_type_selections}
                    onToggle={(v, c) => toggleSelection('user_type_selections', v, c)}
                    disabled={saving}
                    error={errors.user_type_selections}
                    cols="col-6 sm:col-4 md:col-3"
                />
            </SectionPanel>

            {/* ─ Section B — Loan-agent / DSA detail ───────────────── */}
            {isAgentOrDSA && (
                <SectionPanel
                    icon="pi-briefcase"
                    title="Loan agent / DSA details"
                    subtitle="Tell us which products you handle and where you're active."
                >
                    <label className="block text-700 text-xs font-medium mb-1">
                        Loan products you handle
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <CheckboxGrid
                        name="loan_handled"
                        options={LOAN_PRODUCT_OPTIONS}
                        value={value.loan_products_handled}
                        onToggle={(v, c) => toggleSelection('loan_products_handled', v, c)}
                        disabled={saving}
                        error={errors.loan_products_handled}
                        cols="col-6 sm:col-4 md:col-3"
                    />

                    {(value.loan_products_handled?.length || 0) > 0 && (
                        <div className="mb-2 mt-3">
                            <label className="block text-700 text-xs font-medium mb-1">
                                Where are you active?
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <MultiSelect
                                value={value.active_cities}
                                options={activeAreas}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => {
                                    onChange({ active_cities: e.value });
                                    if (errors.active_cities && e.value?.length > 0) {
                                        onErrors({ active_cities: '' });
                                    }
                                }}
                                onFilter={onActiveAreasFilter}
                                filter
                                filterPlaceholder="Type an area name…"
                                placeholder={activeAreasLoading ? 'Loading areas…' : 'Search and pick areas'}
                                display="chip"
                                maxSelectedLabels={5}
                                selectedItemsLabel="{0} areas selected"
                                showSelectAll={false}
                                selectionLimit={50}
                                emptyFilterMessage="No areas found."
                                emptyMessage={activeAreasLoading ? 'Loading areas…' : 'Type to search'}
                                disabled={saving}
                                aria-invalid={!!errors.active_cities}
                                className={classNames('w-full', { 'p-invalid': !!errors.active_cities })}
                                pt={SMALL_PT}
                            />
                            {errors.active_cities && (
                                <small role="alert" className="p-error block mt-1 text-xs">
                                    {errors.active_cities}
                                </small>
                            )}
                        </div>
                    )}
                </SectionPanel>
            )}

            {/* ─ Section C — Referral & support ──────────────────────
                Hidden for company employees — they don't refer leads or
                need support-area matching. */}
            {hasAnySelection && !isCompanyEmployee && (
                <SectionPanel
                    icon="pi-share-alt"
                    title="Referral & support"
                    subtitle="Help us match you with the right partners and lead flow."
                >
                    <label className="block text-700 text-xs font-medium mb-1">Referral loan products</label>
                    <CheckboxGrid
                        name="referral"
                        options={LOAN_PRODUCT_OPTIONS}
                        value={value.referral_loan_products}
                        onToggle={(v, c) => toggleSelection('referral_loan_products', v, c)}
                        disabled={saving}
                        cols="col-6 sm:col-4 md:col-3"
                    />

                    <div className="grid mt-3">
                        <div className="col-12 md:col-6 mb-2">
                            <label className="block text-700 text-xs font-medium mb-1">
                                Monthly disbursements
                            </label>
                            <Dropdown
                                value={value.total_loan_disbursements_per_month}
                                options={DISBURSEMENT_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => onChange({ total_loan_disbursements_per_month: e.value })}
                                placeholder="Select range"
                                showClear
                                disabled={saving}
                                className="w-full"
                                pt={SMALL_PT}
                            />
                        </div>

                        <div className="col-12 md:col-6 mb-2">
                            <label className="block text-700 text-xs font-medium mb-1">
                                Support areas
                            </label>
                            <MultiSelect
                                value={value.support_needed_cities}
                                options={supportAreas}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => onChange({ support_needed_cities: e.value })}
                                onFilter={onSupportAreasFilter}
                                filter
                                filterPlaceholder="Type an area name…"
                                placeholder={supportAreasLoading ? 'Loading areas…' : 'Search and pick areas'}
                                display="chip"
                                maxSelectedLabels={3}
                                selectedItemsLabel="{0} areas selected"
                                showSelectAll={false}
                                selectionLimit={50}
                                emptyFilterMessage="No areas found."
                                emptyMessage={supportAreasLoading ? 'Loading areas…' : 'Type to search'}
                                disabled={saving}
                                className="w-full"
                                pt={SMALL_PT}
                            />
                        </div>
                    </div>
                </SectionPanel>
            )}
        </div>
    );
};

/* ── Section panel — PrimeReact Panel with a custom header.
     Matches BasicInfoStep — slim header with icon chip + title + subtitle. */
const SectionPanel = ({ icon, title, subtitle, children, className = '' }) => {
    const headerContent = (
        <div className="flex align-items-center gap-2">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '1.75rem',
                        height: '1.75rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                        fontSize: '0.85rem',
                    }}
                >
                    <i className={`pi ${icon}`} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-900 font-semibold text-sm">{title}</div>
                {subtitle && (
                    <div className="text-500 font-normal" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Panel
            header={headerContent}
            toggleable={false}
            className={classNames('mb-3', className)}
            pt={{
                header: { className: 'py-2 px-3' },
                content: { className: 'p-3' },
            }}
        >
            {children}
        </Panel>
    );
};

/* ── Compact checkbox grid ───────────────────────────────────────────── */
const CheckboxGrid = ({ name, options, value = [], onToggle, disabled, error, cols }) => (
    <>
        <div className="grid">
            {options.map((opt) => {
                const id = `${name}_${opt.value}`;
                const checked = value.includes(opt.value);
                return (
                    <div key={opt.value} className={classNames('mb-1', cols)}>
                        <div className="flex align-items-center gap-2">
                            <Checkbox
                                inputId={id}
                                value={opt.value}
                                checked={checked}
                                disabled={disabled}
                                onChange={(e) => onToggle(opt.value, e.checked)}
                            />
                            <label htmlFor={id} className="text-700 text-xs cursor-pointer">{opt.label}</label>
                        </div>
                    </div>
                );
            })}
        </div>
        {error && (
            <small role="alert" className="p-error block mt-1 text-xs">{error}</small>
        )}
    </>
);

UserTypeStep.runValidations = (value, opts = {}) => {
    const { isCompanyEmployee = false } = opts;
    const errors = {
        user_type_selections: '',
        loan_products_handled: '',
        active_cities: '',
    };
    const selections = value?.user_type_selections || [];

    // Always required: pick at least one user type
    if (selections.length === 0) {
        errors.user_type_selections = 'Please select at least one option above.';
        return errors;
    }

    // Employees only have the role multi-select; the loan-agent / DSA
    // sub-section isn't rendered for them, so skip those validations.
    if (isCompanyEmployee) {
        return errors;
    }

    // Conditional: when loan_agent or dsa is selected, the products + areas
    // sub-fields become required. Mirrors the form's progressive disclosure.
    const isAgentOrDSA = selections.some((v) => v === 'loan_agent' || v === 'dsa');
    if (isAgentOrDSA) {
        const products = value?.loan_products_handled || [];
        if (products.length === 0) {
            errors.loan_products_handled = 'Pick at least one loan product you handle.';
        }
        // Active areas are required only after products are picked (form
        // hides the field until then — match same gate).
        if (products.length > 0) {
            const areas = value?.active_cities || [];
            if (areas.length === 0) {
                errors.active_cities = 'Pick at least one area where you are active.';
            }
        }
    }

    return errors;
};

UserTypeStep.hasErrors = (errors) =>
    !!errors && Object.values(errors).some((e) => e && e !== '');

export default UserTypeStep;
